import React, { useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { MapPin, Camera, MessageSquare, CheckCircle } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    }
  }, [location]);

  const processSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );

      if (response.data.success) {
        localStorage.setItem('session_token', response.data.session_token);
        setUser(response.data.user);
        window.location.hash = '';
        
        if (response.data.user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Session processing error:', error);
      toast.error('Authentication failed. Please try again.');
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10 px-6 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800">FieldMate</span>
          </div>
          <Button 
            onClick={handleLogin}
            data-testid="header-login-btn"
            className="bg-teal-600 hover:bg-teal-700 text-white px-6"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight">
            Manage Your Field Staff
            <br />
            <span className="text-teal-600">Effortlessly</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-3xl mx-auto">
            Track locations, manage tasks, and communicate with your field team in real-time. 
            Everything you need in one powerful platform.
          </p>
          <Button 
            onClick={handleLogin}
            data-testid="hero-get-started-btn"
            size="lg"
            className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            Get Started with Google
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-16">
            Powerful Features for Field Management
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<MapPin className="w-8 h-8" />}
              title="GPS Tracking"
              description="Mark work locations with precise GPS coordinates"
            />
            <FeatureCard
              icon={<Camera className="w-8 h-8" />}
              title="Photo Upload"
              description="Capture and upload work site photos instantly"
            />
            <FeatureCard
              icon={<MessageSquare className="w-8 h-8" />}
              title="Messaging"
              description="Direct communication between staff and admin"
            />
            <FeatureCard
              icon={<CheckCircle className="w-8 h-8" />}
              title="Task Management"
              description="Assign, track, and close tasks efficiently"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-teal-600 rounded-3xl p-12 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to streamline your field operations?
          </h2>
          <p className="text-lg text-teal-50 mb-4">
            Join teams who trust FieldMate for efficient field staff management.
          </p>
          <p className="text-sm text-teal-100 mb-8 italic">
            First user automatically becomes an admin • Manage your team from day one
          </p>
          <Button 
            onClick={handleLogin}
            data-testid="cta-start-now-btn"
            size="lg"
            className="bg-white text-teal-600 hover:bg-slate-50 px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            Start Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200">
        <div className="max-w-6xl mx-auto text-center text-slate-600">
          <p>&copy; 2025 FieldMate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => {
  return (
    <div className="group p-8 rounded-2xl bg-white border border-slate-200 hover:border-teal-400 hover:shadow-lg transition-all">
      <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600 mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600">{description}</p>
    </div>
  );
};

export default Landing;
